import { AnalyticsBrowser } from '@segment/analytics-next';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { useAuth } from '~/hooks/useAuth';

import { AnalyticsContext, AnalyticsContextType } from '../contexts/analytics-context';
import { mapRolesToUserTypes } from '../utils/user-types';

AnalyticsContext.displayName = 'AnalyticsContext';

const VITE_SEGMENT_KEY = import.meta.env.VITE_SEGMENT_KEY;

export enum AnalyticsEvent {
  LeadsSearched = 'Leads Searched',
}

export default function AnalyticsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [identified, setIdentified] = useState<boolean>(false);

  const analytics = useMemo(() => AnalyticsBrowser.load({ writeKey: VITE_SEGMENT_KEY }), []);
  const analyticsContext: AnalyticsContextType = useMemo(
    () => ({ track: async () => null, page: async () => null, reset: async () => null }),
    [],
  );

  useEffect(() => {
    async function identifyUser() {
      if (analytics) {
        const defaultPayload = (payload?: Record<string, unknown>) => ({
          ...payload,
          sub: user?.sub,
        });
        analyticsContext.track = async (eventName: string, payload?: Record<string, unknown>) => {
          await analytics.track(eventName, defaultPayload(payload));
        };
        analyticsContext.page = async (eventName: string, payload?: Record<string, unknown>) => {
          await analytics.page(eventName, defaultPayload(payload));
        };
        analyticsContext.reset = async () => {
          await analytics.reset();
        };
        if (user?.sub && !identified) {
          analytics.identify(user?.sub, {
            sub: user?.sub,
            email: user?.email,
            user_type: mapRolesToUserTypes(user?.role),
          });
          setIdentified(true);
        }
      }
    }
    identifyUser();
  }, [user, analytics, analyticsContext, identified]);

  return <AnalyticsContext.Provider value={analyticsContext}>{children}</AnalyticsContext.Provider>;
}
